import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { reportApiError } from '../lib/store.js';
import { useBillingCatalog } from '../lib/queries.js';
import { Spinner } from './ui.js';
import { num, eur } from '../lib/format.js';
import { useT } from '../lib/i18n.js';

/**
 * Die Tarifübersicht, an einer Stelle, weil sie an zwei Orten auftaucht:
 * auf der Abrechnungsseite und in der Paywall. Beide zeigen exakt dasselbe,
 * damit ein Tarif nicht zweimal unterschiedlich aussieht.
 *
 * Zur Gestaltung: Jedes Angebot hat seine eigene Farbe (oberer Rand, Name,
 * Credit-Zeile, Knopf), damit man die Stufen auf einen Blick auseinanderhält
 * und beim zweiten Besuch „den grünen" wiederfindet. Empfohlen bleibt
 * trotzdem erkennbar: dickerer Rand und Marke oben rechts.
 */

/**
 * Was ein Tarif kann, was der darunter nicht kann, Zeile für Zeile.
 *
 * Hier stand einmal „Team-Zugänge & API", „White-Label & SLA",
 * „Prioritäts-Rendering" und „Dedizierte Render-Worker". Nichts davon
 * existiert: keine Einladungen, keine öffentliche API, kein Branding, und
 * es läuft genau ein Worker, der im API-Prozess mitfährt. Für 129 Euro im
 * Monat etwas zu versprechen, das es nicht gibt, ist irreführende Werbung
 * — und dem Kunden gegenüber schlicht gelogen.
 *
 * Was hier steht, wird auch durchgesetzt. Wer eine Zeile ergänzt, baut
 * vorher die Prüfung dazu: `maxProjects` in routes/projects.ts,
 * `maxAgentsPerProject` in routes/agents.ts, `maxAccountsPerProject` in
 * routes/social.ts.
 */
const CAPABILITY_ROWS: { label: string; has: (p: PlanRow) => boolean }[] = [
  { label: 'Mehrere Projekte parallel', has: (p) => p.maxProjects > 1 },
  { label: 'Mehrere Rezepte je Projekt, zum Vergleichen', has: (p) => p.key !== 'free' },
  { label: 'Ungenutzte Credits wandern in den Folgemonat', has: (p) => p.key !== 'free' },
  /*
   * „Mehr als zwei Konten je Projekt" stand hier für drei Tarife mit Haken.
   * Jeder Tarif erlaubt genau zwei (maxAccountsPerProject), und der Server
   * setzt das durch -- die Zeile versprach etwas, das es nicht gibt.
   */
];

export interface PlanRow {
  key: string;
  name: string;
  tagline?: string;
  /** Erkennungsfarbe der Karte (.offer-* in theme.css) */
  accent?: string;
  popular?: boolean;
  priceEur: number;
  monthlyCredits: number;
  maxProjects: number;
  maxAccountsPerProject?: number;
  features: string[];
}

/** Ersatzwert, solange der Katalog lädt: rund 110 Credits je Bild-Post mit
 *  drei Szenen. Danach gilt der Wert, den die Preis-Engine im Server liefert. */
const CREDITS_PER_POST_FALLBACK = 110;

export function PlanCards({
  currentPlan,
  highlight,
  compact: compactMode = false,
  onPurchaseStart,
  hasSubscription = false,
}: {
  currentPlan?: string;
  /** Tarif, der hervorgehoben wird, z. B. der, der die Grenze auflöst */
  highlight?: string | null;
  /** Weniger Beiwerk, für die Paywall */
  compact?: boolean;
  onPurchaseStart?: () => void;
  hasSubscription?: boolean;
}) {
  const t = useT();
  const { data: catalog } = useBillingCatalog();
  const [busy, setBusy] = useState<string | null>(null);

  const plans = (catalog?.plans ?? []) as PlanRow[];
  const paid = plans.filter((p) => p.key !== 'free');
  // Ungefähr, wie viele Bild-Posts das Monatsguthaben trägt: macht Credits greifbar.
  const creditsPerPost = catalog?.examples[0]?.credits || CREDITS_PER_POST_FALLBACK;

  async function upgrade(planKey: string) {
    setBusy(planKey);
    onPurchaseStart?.();
    try {
      const res = await api.post<{ url: string }>('/billing/checkout/subscription', { planKey });
      window.location.href = res.url;
    } catch (err) {
      reportApiError(err, err instanceof ApiError ? err.message : 'Wechsel nicht möglich');
      setBusy(null);
    }
  }

  if (!plans.length) {
    return <div className="dim" style={{ padding: 20, textAlign: 'center' }}>{t('Tarife werden geladen…')}</div>;
  }

  return (
    <div className="col" style={{ gap: 14 }}>
      {catalog && !catalog.stripeEnabled && (
        <div className="note note-warn">
          <span className="note-icon">!</span>
          <div>
            {t('Zahlungen sind gerade nicht eingerichtet. Im Backend fehlt')}{' '}
            <span className="mono">STRIPE_SECRET_KEY</span>{t('. Die Tarife stehen hier trotzdem, damit du siehst, was jeweils drin ist.')}
          </div>
        </div>
      )}
      {hasSubscription && (
        <div className="note note-warn">
          <span className="note-icon">!</span>
          <div>{t('Du hast bereits ein Abo. Ein Tarifwechsel ist noch nicht möglich; mehr Credits bekommst du sofort mit einem Credit-Paket.')}</div>
        </div>
      )}

      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${compactMode ? 206 : 250}px, 1fr))`, gap: 12 }}
      >
        {paid.map((plan) => {
          const isCurrent = plan.key === currentPlan;
          const isHighlight = highlight ? plan.key === highlight : plan.popular;
          const posts = Math.round(plan.monthlyCredits / creditsPerPost);

          return (
            <div
              key={plan.key}
              className={`plan offer offer-${plan.accent ?? 'slate'} ${isCurrent ? 'plan-current' : ''} ${isHighlight && !isCurrent ? 'plan-popular' : ''}`}
            >
              {isCurrent ? (
                <span className="plan-badge bl-badge-current">{t('Dein Tarif')}</span>
              ) : isHighlight ? (
                <span className="plan-badge">{highlight ? t('Empfohlen') : t('Beliebt')}</span>
              ) : null}

              <div>
                <div className="plan-name">{plan.name}</div>
                {plan.tagline && <div className="plan-tagline" style={{ marginTop: 3 }}>{plan.tagline}</div>}
              </div>

              <div className="plan-credits">
                <div className="plan-credits-num">{t('{n} Credits / Monat', { n: num(plan.monthlyCredits) })}</div>
                <div className="dim" style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.6 }}>
                  {t('≈ {n} fertige Bild-Beiträge', { n: num(posts) })}
                  <br />
                  {t('bis zu {n}', { n: plan.maxProjects })} {plan.maxProjects === 1 ? t('Projekt') : t('Projekte')}
                  {plan.maxAccountsPerProject ? t(' · {n} Accounts je Projekt', { n: plan.maxAccountsPerProject }) : ''}
                </div>
              </div>

              <div className="plan-price">
                <span className="plan-price-num">{eur(plan.priceEur)}</span>
                <span className="plan-price-unit">{t('pro Monat')}</span>
              </div>

              <button
                className={`plan-cta ${isCurrent ? 'plan-cta-ghost' : ''}`}
                onClick={() => void upgrade(plan.key)}
                disabled={isCurrent || hasSubscription || !!busy || !catalog?.stripeEnabled}
              >
                {busy === plan.key && <Spinner size={13} />}
                {isCurrent ? t('Aktueller Tarif') : hasSubscription ? t('Tarifwechsel noch nicht möglich') : t('{name} holen', { name: plan.name })}
              </button>

              <div className="col" style={{ gap: 7, marginTop: 2 }}>
                {CAPABILITY_ROWS.map((row) => {
                  const has = row.has(plan);
                  return (
                    <div key={row.label} className={`plan-feat ${has ? '' : 'plan-feat-off'}`}>
                      <span className="plan-feat-mark">{has ? '✓' : '✕'}</span>
                      <span>{t(row.label)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!compactMode && (
        <div className="dim" style={{ fontSize: 12, lineHeight: 1.6 }}>
          {t('Monatlich kündbar. Nicht verbrauchte Credits wandern bis zur Tarifgrenze in den Folgemonat. Reicht das Monatsguthaben nicht, lädst du jederzeit ein Credit-Paket nach, das läuft nicht ab.')}
        </div>
      )}

      {/*
        * Steht bewusst AUSSERHALB der compactMode-Bedingung: Auch die Paywall
        * zeigt Preise, und wo ein Preis steht, muss die Angabe zur
        * Umsatzsteuer daneben stehen. Der Satz kommt vom Server, damit er
        * nicht irgendwann etwas anderes behauptet als Checkout und Rechnung.
        */}
      {catalog?.taxNote && (
        <div className="dim" style={{ fontSize: 12, lineHeight: 1.6 }}>{catalog.taxNote}</div>
      )}
    </div>
  );
}
